'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridSolidEdgesProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridSolidEdges - Clean grid pattern visible on edges, fading to center
 *
 * Continuous 1px lines forming a square grid that is visible around
 * the edges and fades out toward the center. Inverse of GridSolidCenter.
 *
 * Fixed fadeSize: 0.70
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridSolidEdges />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridSolidEdges({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridSolidEdgesProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.7
  const size = `${fadeSize * 100}%`
  const stop1 = `${fadeSize * 100 * 0.45}%`
  const stop2 = `${fadeSize * 100 * 0.85}%`
  const stop3 = `${fadeSize * 100 * 1.15}%`
  const maskGradient = `radial-gradient(ellipse ${size} ${size} at 50% 50%, transparent 0%, transparent ${stop1}, rgba(0,0,0,0.4) ${stop2}, rgba(0,0,0,0.8) ${stop3}, #000 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px),
          linear-gradient(to bottom, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
